import type { MonthlyData, Zone } from 'types/match'

import { useState, useEffect } from 'react'
import styled from 'styled-components'

import { classNames } from 'utils'
import copy from 'copy.json'

import Tooltip from 'pages/matches/Tooltip'

type Props = {
  data: MonthlyData[]
  onShowZones: (zones: Zone[]) => void
}

const { months } = copy.common
const texts = copy.page.matches.graph

export default function MonthlyDataGraph({ data, onShowZones }: Props) {
  const [hoveredItem, setHoveredItem] = useState(-1)

  useEffect(() => {
    const resetHover = () => setHoveredItem(-1)

    window.addEventListener('scroll', resetHover)

    return () => window.removeEventListener('scroll', resetHover)
  }, [])

  const summedValue = data.map(
    entry => entry.total_coverage + entry.total_surplus + entry.total_shortage
  )

  const biggestValue = Math.max(...summedValue)

  return (
    <Wrapper onMouseLeave={() => setHoveredItem(-1)}>
      <h3>{texts.heading}</h3>

      <Bars className="bars">
        {data.map((entry, index) => {
          const sum = summedValue[index]
          const barHeight = (sum / biggestValue) * 100
          const surplusHeight = Math.round((entry.total_surplus / sum) * 100)
          const shortageHeight = Math.round((entry.total_shortage / sum) * 100)

          return (
            <Entry
              key={index}
              className={classNames(hoveredItem > -1 && hoveredItem !== index && 'dimmed')}
            >
              <BarWrapper>
                <Bar
                  className="bar"
                  style={{ height: barHeight + '%' }}
                  onMouseEnter={() => setHoveredItem(index)}
                >
                  <Surplus style={{ height: surplusHeight + '%' }} />
                  <Shortage style={{ height: shortageHeight + '%' }} />
                </Bar>
              </BarWrapper>

              <Month>{months[index].slice(0, 3)}</Month>
            </Entry>
          )
        })}
      </Bars>

      {hoveredItem > -1 && (
        <Tooltip hoveredItem={hoveredItem} entry={data[hoveredItem]} onShowZones={onShowZones} />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  max-width: 100%;

  @media (min-width: 769px) {
    padding: 0 clamp(0px, 8vw, 25%);
  }
`

const Bars = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  overflow-y: hidden;
`

const Entry = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;

  &.dimmed {
    opacity: 0.2;
  }
`

const BarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 24px;
  height: 200px;
  overflow: hidden;
`

const Bar = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: var(--green);
  border-radius: 4px;
  overflow: hidden;
`

const Surplus = styled.div`
  background: var(--blue);
`

const Shortage = styled.div`
  background: var(--red);
`

const Month = styled.span`
  color: var(--gray);
  font: var(--600-12);
`
